import {
  Button,
  Heading,
  Input,
  Sheet,
  SheetContent,
  FormSelect,
  Skeleton,
} from '@/components/atoms';
import {
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/molecules';
import {
  useProductCategories,
  useProductSubcategories,
  useProducts,
} from '@/api';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ProductCreationForm,
  ProductMetrics,
  AllProducts,
  InStoreProducts,
  OutOfStockProducts,
  CategoryManagementModal,
  ProductsEmptyState,
  AddProductMethodModal,
  ProductUploadsTab,
} from './components';
import { Plus, Search, Tags } from 'lucide-react';
import { routes } from '@/lib/constants';
import { usePlanGate } from '@/components/organisms';

export const ProductsPage = () => {
  const navigate = useNavigate();
  const { allowsFeature, allowsCount } = usePlanGate();
  const [activeTab, setActiveTab] = useState('all');
  const [id, setId] = useState<number>();
  const [open, setOpen] = useState(false);
  const [addMethodModalOpen, setAddMethodModalOpen] = useState(false);
  const [mode, setMode] = useState<'update' | 'create' | 'view'>('create');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilterId, setCategoryFilterId] = useState('');
  const [subcategoryFilterId, setSubcategoryFilterId] = useState('');
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  const { data: products = [], isLoading: isLoadingProducts } = useProducts();
  const { data: categories = [] } = useProductCategories();
  const numericCategoryFilterId = categoryFilterId
    ? Number(categoryFilterId)
    : undefined;
  const { data: subcategories = [] } = useProductSubcategories(
    numericCategoryFilterId,
    !!numericCategoryFilterId
  );

  const hasProducts = products.length > 0;
  const isProductCatalogTab = activeTab !== 'gallery';

  const categoryFilterOptions = useMemo(
    () =>
      categories.map((category) => ({
        value: String(category.id),
        label: category.name,
      })),
    [categories]
  );

  const subcategoryFilterOptions = useMemo(
    () =>
      subcategories.map((subcategory) => ({
        value: String(subcategory.id),
        label: subcategory.name,
      })),
    [subcategories]
  );

  const categoryId = numericCategoryFilterId;
  const subcategoryId = subcategoryFilterId
    ? Number(subcategoryFilterId)
    : undefined;

  const hasCategoryFilters = !!categoryFilterId || !!subcategoryFilterId;

  const toggleOpen = () => setOpen((prev) => !prev);

  const canAddAnotherProduct = () =>
    allowsCount('productUploads', products.length + 1);

  const openAddMethodModal = () => {
    if (!canAddAnotherProduct()) return;
    setAddMethodModalOpen(true);
  };

  const openCreateProduct = () => {
    if (!canAddAnotherProduct()) return;
    setId(undefined);
    setMode('create');
    setOpen(true);
  };

  const handleAddManually = () => {
    openCreateProduct();
  };

  const handleBulkUpload = () => {
    if (!allowsFeature('bulkUpload')) return;
    navigate(routes.productsBulkUpload);
  };

  const clearCategoryFilters = () => {
    setCategoryFilterId('');
    setSubcategoryFilterId('');
  };

  if (isLoadingProducts) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-16 w-full max-w-md" />
        <Skeleton className="h-80 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <PageHeader
          heading="Products"
          description="Manage your products and inventory"
        />
        {(hasProducts || isProductCatalogTab) && (
          <div className="flex flex-wrap items-center gap-3 lg:shrink-0">
            {hasProducts && (
              <Button
                variant="outline"
                onClick={() => setCategoryModalOpen(true)}
              >
                <Tags />
                Manage Categories
              </Button>
            )}
            <Button onClick={openAddMethodModal}>
              <Plus />
              Add Product
            </Button>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto py-2">
          <TabsList>
            {hasProducts ? (
              <>
                <TabsTrigger value="all">All products</TabsTrigger>
                <TabsTrigger value="in-store">Products in store</TabsTrigger>
                <TabsTrigger value="out-of-stock">Out of stock</TabsTrigger>
              </>
            ) : (
              <TabsTrigger value="all">Products</TabsTrigger>
            )}
            <TabsTrigger value="gallery">Gallery</TabsTrigger>
          </TabsList>
        </div>

        {hasProducts && isProductCatalogTab && (
          <>
            <ProductMetrics />
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <div className="flex flex-1 items-center overflow-hidden rounded-md border border-input bg-white">
                <Search className="ml-3 size-4 shrink-0 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search products by name, description, or category..."
                  borderless
                  className="rounded-none"
                />
              </div>
              <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:w-auto lg:min-w-[320px]">
                <FormSelect
                  placeholder="All categories"
                  value={categoryFilterId}
                  onChange={(value) => {
                    setCategoryFilterId(value);
                    setSubcategoryFilterId('');
                  }}
                  options={categoryFilterOptions}
                />
                <FormSelect
                  placeholder="All subcategories"
                  value={subcategoryFilterId}
                  onChange={setSubcategoryFilterId}
                  options={subcategoryFilterOptions}
                  disabled={!categoryFilterId}
                />
              </div>
              {hasCategoryFilters && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={clearCategoryFilters}
                  className="lg:shrink-0"
                >
                  Clear filters
                </Button>
              )}
            </div>
          </>
        )}

        {!hasProducts && isProductCatalogTab && (
          <TabsContent value="all">
            <ProductsEmptyState onAddProduct={openAddMethodModal} />
          </TabsContent>
        )}

        {hasProducts && (
          <>
            <AllProducts
              onAddProduct={openAddMethodModal}
              searchQuery={searchQuery}
              categoryId={categoryId}
              subcategoryId={subcategoryId}
              onView={(pid) => {
                setId(pid);
                setMode('view');
                setOpen(true);
              }}
              onEdit={(pid) => {
                setId(pid);
                setMode('update');
                setOpen(true);
              }}
            />
            <InStoreProducts
              onAddProduct={openAddMethodModal}
              searchQuery={searchQuery}
              categoryId={categoryId}
              subcategoryId={subcategoryId}
              onView={(pid) => {
                setId(pid);
                setMode('view');
                setOpen(true);
              }}
              onEdit={(pid) => {
                setId(pid);
                setMode('update');
                setOpen(true);
              }}
            />
            <OutOfStockProducts
              onAddProduct={openAddMethodModal}
              searchQuery={searchQuery}
              categoryId={categoryId}
              subcategoryId={subcategoryId}
              onView={(pid) => {
                setId(pid);
                setMode('view');
                setOpen(true);
              }}
              onEdit={(pid) => {
                setId(pid);
                setMode('update');
                setOpen(true);
              }}
            />
          </>
        )}

        <ProductUploadsTab />
      </Tabs>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="max-sm:w-screen overflow-y-auto pt-10 pb-4 px-4">
          <Heading size="xl">
            {mode === 'create'
              ? 'Create Product'
              : mode === 'update'
                ? 'Edit Product'
                : 'View Product'}
          </Heading>
          <ProductCreationForm id={id} mode={mode} toggleOpen={toggleOpen} />
        </SheetContent>
      </Sheet>

      <AddProductMethodModal
        open={addMethodModalOpen}
        onOpenChange={setAddMethodModalOpen}
        onAddManually={handleAddManually}
        onBulkUpload={handleBulkUpload}
      />

      <CategoryManagementModal
        open={categoryModalOpen}
        onOpenChange={setCategoryModalOpen}
      />
    </div>
  );
};
